import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Aggregation } from '@/types/query';
import type { ColumnMeta } from '@/types/dataset';
import type { WidgetConfig, WidgetKind } from '@/types/widget';
import {
  aggregationLabelKey,
  aggregationsForColumn,
  DEFAULT_BREAKDOWN_LIMIT,
  DEFAULT_CHART_LIMIT,
} from '@/engine/widget-spec';
import { useModeStore } from '@/store/mode-store';
import type { DashboardMode } from '@/persistence/config-db';

type QueryBuilderFormProps = {
  /** Called after a widget is added; also wired to the Cancel button. */
  onClose: () => void;
  datasetId: string;
  columns: ColumnMeta[];
  onAdd: (cfg: WidgetConfig) => void;
};

type KindOption = {
  value: WidgetKind;
  /** Suffix of `queryBuilder.kind.<labelKey>` — resolved through i18n. */
  labelKey: 'kpi' | 'bar' | 'line' | 'pie' | 'compareTable';
  /** Modes where this kind is selectable. Other modes show it disabled. */
  availableIn: ReadonlyArray<DashboardMode>;
};

/**
 * Every kind we render — visible in both modes, but disabled (with a
 * tooltip explaining why) in modes where the kind isn't applicable.
 * `pie` is normal-only because the pivoted A/B aggregate doesn't fit
 * a pie's single-series model; `compare-table` is compare-only because
 * its rows are deltas across the two configured periods.
 */
/**
 * Display labels resolve through i18n at render time
 * (`t('queryBuilder.kind.<value>')`); declaring them in this list as
 * the i18n key suffix keeps the data shape stable without leaking
 * hard-coded TR strings.
 */
const ALL_KIND_OPTIONS: ReadonlyArray<KindOption> = [
  { value: 'kpi', labelKey: 'kpi', availableIn: ['normal', 'compare'] },
  { value: 'bar', labelKey: 'bar', availableIn: ['normal', 'compare'] },
  { value: 'line', labelKey: 'line', availableIn: ['normal', 'compare'] },
  { value: 'pie', labelKey: 'pie', availableIn: ['normal'] },
  {
    value: 'compare-table',
    labelKey: 'compareTable',
    availableIn: ['compare'],
  },
];

const MULTI_CAPABLE_KINDS: WidgetKind[] = ['bar', 'line'];

function defaultTitle(
  t: TFunction,
  kind: WidgetKind,
  metricColumn: string,
  aggregation: Aggregation,
  groupBy: string | undefined,
): string {
  const agg = t(aggregationLabelKey(aggregation));
  if (kind === 'kpi' || !groupBy) {
    return t('queryBuilder.defaultTitle.simple', { agg, col: metricColumn });
  }
  return t('queryBuilder.defaultTitle.grouped', {
    agg,
    col: metricColumn,
    group: groupBy,
  });
}

function genId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

type ExtraMetric = {
  column: string;
  aggregation: Aggregation;
};

/**
 * Inline form for building a new widget — rendered directly inside the
 * sidebar's "Widget Ekle" sub-panel (Phase 11 batch 2.3). Previously
 * wrapped in a modal portal; the modal shell was removed so the form
 * lives natively in the sub-panel and the sidebar's existing close
 * affordances govern dismissal.
 */
export function QueryBuilderForm({
  onClose,
  datasetId,
  columns,
  onAdd,
}: QueryBuilderFormProps): JSX.Element {
  const { t } = useTranslation();
  const mode = useModeStore((s) => s.mode);
  type RenderableKind = KindOption & {
    disabled: boolean;
    /** Tooltip explaining the disabled state — empty when enabled. */
    title: string;
  };
  const KIND_OPTIONS = useMemo<ReadonlyArray<RenderableKind>>(
    () =>
      ALL_KIND_OPTIONS.map((opt) => {
        const enabled = opt.availableIn.includes(mode);
        const title = enabled
          ? ''
          : mode === 'compare'
          ? t('compare.disabledInCompare')
          : t('compare.disabledInNormal');
        return { ...opt, disabled: !enabled, title };
      }),
    [mode, t],
  );
  const [kind, setKind] = useState<WidgetKind>('bar');
  const [groupBy, setGroupBy] = useState<string>(columns[0]?.name ?? '');
  const [metricColumn, setMetricColumn] = useState<string>(
    columns[1]?.name ?? columns[0]?.name ?? '',
  );
  const [aggregation, setAggregation] = useState<Aggregation>('count');
  const [extraMetrics, setExtraMetrics] = useState<ExtraMetric[]>([]);
  const [breakdownBy, setBreakdownBy] = useState<string>('');
  const [breakdownLimit, setBreakdownLimit] = useState<number>(
    DEFAULT_BREAKDOWN_LIMIT,
  );
  const [limit, setLimit] = useState<number>(DEFAULT_CHART_LIMIT);
  const [title, setTitle] = useState<string>('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [rowCountKpi, setRowCountKpi] = useState(false);
  // Pie-only: show the "Toplam: N" badge from the moment the widget is
  // first rendered, instead of forcing the user to open Widget Settings
  // after creation.
  const [pieShowTotal, setPieShowTotal] = useState(false);

  // Reset the form whenever the column set changes (active dataset
  // swap or schema rebuild). The sub-panel mount cycle handles the
  // initial-open reset implicitly: switching nav unmounts the form.
  useEffect(() => {
    const firstCol = columns[0]?.name ?? '';
    const secondCol = columns[1]?.name ?? firstCol;
    setKind('bar');
    setGroupBy(firstCol);
    setMetricColumn(secondCol);
    setAggregation('count');
    setExtraMetrics([]);
    setBreakdownBy('');
    setBreakdownLimit(DEFAULT_BREAKDOWN_LIMIT);
    setLimit(DEFAULT_CHART_LIMIT);
    setTitleTouched(false);
    setRowCountKpi(false);
    setPieShowTotal(false);
  }, [columns]);

  const metricCol = columns.find((c) => c.name === metricColumn);
  const availableAggs = useMemo(
    () => aggregationsForColumn(metricCol),
    [metricCol],
  );

  useEffect(() => {
    if (!availableAggs.includes(aggregation)) {
      setAggregation(availableAggs[0] ?? 'count');
    }
  }, [availableAggs, aggregation]);

  const isMulti = MULTI_CAPABLE_KINDS.includes(kind);

  const suggestedTitle = rowCountKpi
    ? t('queryBuilder.rowCountKpi.defaultTitle')
    : defaultTitle(
        t,
        kind,
        metricColumn,
        aggregation,
        kind === 'kpi' ? undefined : groupBy,
      );
  const effectiveTitle = titleTouched ? title : suggestedTitle;

  const canSubmit =
    rowCountKpi ||
    (metricColumn.length > 0 && (kind === 'kpi' || groupBy.length > 0));

  const handleSubmit = (): void => {
    if (!canSubmit) return;

    // Row count KPI shortcut — counts all rows of the first column.
    if (rowCountKpi) {
      const firstCol = columns[0]?.name ?? '';
      if (!firstCol) return;
      onAdd({
        id: genId('kpi'),
        datasetId,
        mode,
        kind: 'kpi',
        title: effectiveTitle,
        metric: { column: firstCol, aggregation: 'count' },
      });
      onClose();
      return;
    }

    const breakdownActive =
      isMulti &&
      mode !== 'compare' &&
      !!breakdownBy &&
      extraMetrics.length === 0;
    const base: WidgetConfig =
      kind === 'kpi'
        ? {
            id: genId('kpi'),
            datasetId,
            mode,
            kind: 'kpi',
            title: effectiveTitle,
            metric: { column: metricColumn, aggregation },
          }
        : {
            id: genId(kind),
            datasetId,
            mode,
            kind,
            title: effectiveTitle,
            groupBy,
            metric: { column: metricColumn, aggregation },
            ...(isMulti && extraMetrics.length > 0
              ? { extraMetrics }
              : {}),
            ...(breakdownActive
              ? {
                  breakdownBy,
                  breakdownLimit,
                }
              : {}),
            // Pie's "Toplam: N" toggle is the only pie-specific knob
            // exposed at creation time — the rest stay defaulted and
            // can be tweaked from Widget Settings later.
            ...(kind === 'pie' && pieShowTotal
              ? { chartOptions: { pieShowTotal: true } }
              : {}),
            limit,
          };
    onAdd(base);
    onClose();
  };

  return (
    <div className="flex flex-col gap-3" style={{ color: 'var(--color-fg)' }}>
        {/* Widget kind */}
        <div>
          <label className="block text-xs mb-1" style={{ color: 'var(--color-muted)' }}>
            {t('queryBuilder.kindLabel')}
          </label>
          <div className="flex gap-1 flex-wrap">
            {KIND_OPTIONS.map((opt) => {
              const active = kind === opt.value && !opt.disabled;
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={opt.disabled}
                  title={opt.title}
                  aria-disabled={opt.disabled}
                  onClick={() => {
                    if (!opt.disabled) setKind(opt.value);
                  }}
                  className="flex-1 min-w-[70px] text-xs px-2 py-1 rounded disabled:cursor-not-allowed"
                  style={{
                    background: active ? 'var(--color-accent)' : 'transparent',
                    color: active
                      ? 'white'
                      : opt.disabled
                      ? 'var(--color-muted)'
                      : 'var(--color-fg)',
                    border: '1px solid var(--color-border)',
                    opacity: opt.disabled ? 0.45 : 1,
                  }}
                >
                  {t(`queryBuilder.kind.${opt.labelKey}`)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Row count KPI shortcut — only within the KPI tab */}
        {kind === 'kpi' && (
          <label
            className="flex items-center gap-2 text-xs cursor-pointer px-2 py-1.5 rounded"
            style={{
              border: '1px solid var(--color-border)',
              background: rowCountKpi ? 'var(--color-accent-subtle)' : 'transparent',
            }}
          >
            <input
              type="checkbox"
              checked={rowCountKpi}
              onChange={(e) => setRowCountKpi(e.target.checked)}
            />
            <span>
              <span className="font-medium">
                {t('queryBuilder.rowCountKpi.title')}
              </span>{' '}
              <span style={{ color: 'var(--color-muted)' }}>
                {t('queryBuilder.rowCountKpi.hint')}
              </span>
            </span>
          </label>
        )}

        {!rowCountKpi && (
          <>
            {/* Group-by column */}
            {kind !== 'kpi' && (
              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--color-muted)' }}>
                  {t('queryBuilder.groupBy')}
                </label>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  className="w-full text-xs p-1 rounded"
                  style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
                >
                  {columns.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name} ({c.sqlType})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Metric column */}
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--color-muted)' }}>
                {t('queryBuilder.metricColumn')}
              </label>
              <select
                value={metricColumn}
                onChange={(e) => setMetricColumn(e.target.value)}
                className="w-full text-xs p-1 rounded"
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
              >
                {columns.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name} ({c.sqlType})
                  </option>
                ))}
              </select>
            </div>

            {/* Aggregation */}
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--color-muted)' }}>
                {t('queryBuilder.aggregation')}
              </label>
              <select
                value={aggregation}
                onChange={(e) => setAggregation(e.target.value as Aggregation)}
                className="w-full text-xs p-1 rounded"
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
              >
                {availableAggs.map((a) => (
                  <option key={a} value={a}>
                    {t(aggregationLabelKey(a))}
                  </option>
                ))}
              </select>
            </div>

            {/* Extra metrics — multi-series charts only */}
            {isMulti && (
              <ExtraMetricsEditor
                columns={columns}
                metrics={extraMetrics}
                onChange={setExtraMetrics}
                disabled={!!breakdownBy}
              />
            )}

            {/* Seri kırılımı — bar / line only, mutually exclusive with
                extra metrics (the breakdown takes over the second
                grouping dimension). Hidden entirely in compare mode:
                the __period__ column already owns the series dimension
                so adding a breakdown would conflict, and the spec
                builder gates it server-side anyway. */}
            {isMulti && mode !== 'compare' && (
              <BreakdownEditor
                columns={columns}
                breakdownBy={breakdownBy}
                breakdownLimit={breakdownLimit}
                onBreakdownByChange={setBreakdownBy}
                onLimitChange={setBreakdownLimit}
                disabled={extraMetrics.length > 0}
              />
            )}

            {/* Pasta — toplam veri sayısı badge'i. Yalnızca pie için
                görünür; diğer kind'lerde anlamsız. */}
            {kind === 'pie' && (
              <label
                className="flex items-center gap-2 text-xs cursor-pointer"
                style={{ color: 'var(--color-fg)' }}
              >
                <input
                  type="checkbox"
                  checked={pieShowTotal}
                  onChange={(e) => setPieShowTotal(e.target.checked)}
                />
                {t('widgetSettings.pieShowTotal')}
              </label>
            )}

            {/* Limit — charts only. `limit === 0` means unlimited; the
                checkbox toggles that sentinel without losing the previous
                numeric value. Compare-table caps at 10 because each
                winners/losers side is meant to be a short highlight
                list (item: satır limiti 10 olsun). */}
            {kind !== 'kpi' && (
              <div>
                <label
                  className="block text-xs mb-1"
                  style={{ color: 'var(--color-muted)' }}
                >
                  {t('queryBuilder.limit')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={kind === 'compare-table' ? 10 : 500}
                    value={limit === 0 ? '' : limit}
                    disabled={limit === 0}
                    onChange={(e) => {
                      const cap = kind === 'compare-table' ? 10 : 500;
                      setLimit(
                        Math.max(1, Math.min(cap, Number(e.target.value) || 1)),
                      );
                    }}
                    className="flex-1 text-xs p-1 rounded disabled:opacity-50"
                    style={{
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-bg)',
                    }}
                  />
                  <label
                    className="flex items-center gap-1 text-xs cursor-pointer shrink-0"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    <input
                      type="checkbox"
                      checked={limit === 0}
                      onChange={(e) =>
                        setLimit(e.target.checked ? 0 : DEFAULT_CHART_LIMIT)
                      }
                    />
                    {t('queryBuilder.unlimited')}
                  </label>
                </div>
              </div>
            )}
          </>
        )}

        {/* Title */}
        <div>
          <label className="block text-xs mb-1" style={{ color: 'var(--color-muted)' }}>
            {t('queryBuilder.titleField')}
          </label>
          <input
            type="text"
            value={effectiveTitle}
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleTouched(true);
            }}
            className="w-full text-xs p-1 rounded"
            style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
          />
        </div>

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="text-xs px-3 py-1 rounded"
          style={{ color: 'var(--color-muted)' }}
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="text-xs px-3 py-1 rounded text-white disabled:opacity-50"
          style={{ background: 'var(--color-accent)' }}
        >
          {t('common.add')}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Extra metrics editor (for bar / line multi-series)
// ---------------------------------------------------------------------------

type ExtraMetricsEditorProps = {
  columns: ColumnMeta[];
  metrics: ExtraMetric[];
  onChange: (next: ExtraMetric[]) => void;
  /** Disabled when a Seri kırılımı column is active (mutual exclusion). */
  disabled?: boolean;
};

function ExtraMetricsEditor({
  columns,
  metrics,
  onChange,
  disabled = false,
}: ExtraMetricsEditorProps): JSX.Element {
  const { t } = useTranslation();
  function addMetric(): void {
    const col = columns[0]?.name ?? '';
    if (!col) return;
    onChange([...metrics, { column: col, aggregation: 'count' }]);
  }
  function removeAt(i: number): void {
    onChange(metrics.filter((_, idx) => idx !== i));
  }
  function updateAt(i: number, patch: Partial<ExtraMetric>): void {
    const next = metrics.map((m, idx) => (idx === i ? { ...m, ...patch } : m));
    onChange(next);
  }

  return (
    <div
      className="flex flex-col gap-2 pt-2 border-t"
      style={{ borderColor: 'var(--color-border)' }}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium" style={{ color: 'var(--color-muted)' }}>
          {t('queryBuilder.extraMetrics.title')}
        </span>
        <button
          type="button"
          onClick={addMetric}
          disabled={disabled}
          title={disabled ? t('queryBuilder.breakdown.conflictWithExtra') : ''}
          className="text-xs px-2 py-0.5 rounded hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            color: 'var(--color-accent)',
            border: '1px dashed var(--color-accent)',
          }}
        >
          {t('queryBuilder.extraMetrics.addButton')}
        </button>
      </div>

      {metrics.length === 0 && (
        <span className="text-[11px]" style={{ color: 'var(--color-muted)' }}>
          {disabled
            ? t('queryBuilder.breakdown.conflictWithExtra')
            : t('queryBuilder.extraMetrics.hint')}
        </span>
      )}

      {metrics.map((m, i) => {
        const col = columns.find((c) => c.name === m.column);
        const aggs = aggregationsForColumn(col);
        return (
          <div key={i} className="flex gap-1">
            <select
              value={m.column}
              onChange={(e) => updateAt(i, { column: e.target.value })}
              className="flex-1 text-xs p-1 rounded"
              style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
            >
              {columns.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              value={m.aggregation}
              onChange={(e) => updateAt(i, { aggregation: e.target.value as Aggregation })}
              className="text-xs p-1 rounded"
              style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
            >
              {aggs.map((a) => (
                <option key={a} value={a}>
                  {t(aggregationLabelKey(a))}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => removeAt(i)}
              aria-label={t('queryBuilder.extraMetrics.removeAria')}
              className="px-2 rounded hover:opacity-80"
              style={{ color: 'var(--color-danger)' }}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Seri kırılımı editor (bar / line breakdown by a second column)
// ---------------------------------------------------------------------------

type BreakdownEditorProps = {
  columns: ColumnMeta[];
  breakdownBy: string;
  breakdownLimit: number;
  onBreakdownByChange: (next: string) => void;
  onLimitChange: (next: number) => void;
  /** Disabled while the user has extra metrics configured (mutual exclusion). */
  disabled?: boolean;
};

/**
 * "Seri kırılımı" panel: pick a categorical column whose distinct
 * values become separate coloured series on a bar / line chart. The
 * existing groupBy column stays as the X-axis; the breakdown becomes
 * the series dimension. Mutually exclusive with extra metrics.
 */
function BreakdownEditor({
  columns,
  breakdownBy,
  breakdownLimit,
  onBreakdownByChange,
  onLimitChange,
  disabled = false,
}: BreakdownEditorProps): JSX.Element {
  const { t } = useTranslation();
  const active = !!breakdownBy && !disabled;

  return (
    <div
      className="flex flex-col gap-2 pt-2 border-t"
      style={{ borderColor: 'var(--color-border)' }}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium" style={{ color: 'var(--color-muted)' }}>
          {t('queryBuilder.breakdown.title')}
        </span>
        {!active ? (
          <button
            type="button"
            onClick={() => {
              const firstCol = columns[0]?.name ?? '';
              if (firstCol) onBreakdownByChange(firstCol);
            }}
            disabled={disabled}
            title={disabled ? t('queryBuilder.breakdown.conflictWithExtra') : ''}
            className="text-xs px-2 py-0.5 rounded hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              color: 'var(--color-accent)',
              border: '1px dashed var(--color-accent)',
            }}
          >
            {t('queryBuilder.breakdown.enable')}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onBreakdownByChange('')}
            className="text-xs px-2 py-0.5 rounded hover:opacity-80"
            style={{ color: 'var(--color-danger)' }}
          >
            {t('queryBuilder.breakdown.disable')}
          </button>
        )}
      </div>

      {!active && (
        <span className="text-[11px]" style={{ color: 'var(--color-muted)' }}>
          {disabled
            ? t('queryBuilder.breakdown.conflictWithExtra')
            : t('queryBuilder.breakdown.hint')}
        </span>
      )}

      {active && (
        <div className="flex flex-col gap-1">
          <label className="block text-[11px]" style={{ color: 'var(--color-muted)' }}>
            {t('queryBuilder.breakdown.column')}
          </label>
          <select
            value={breakdownBy}
            onChange={(e) => onBreakdownByChange(e.target.value)}
            className="w-full text-xs p-1 rounded"
            style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
          >
            {columns.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name} ({c.sqlType})
              </option>
            ))}
          </select>

          <label
            className="block text-[11px] mt-1"
            style={{ color: 'var(--color-muted)' }}
          >
            {t('queryBuilder.breakdown.limit')}
          </label>
          <input
            type="number"
            min={1}
            max={50}
            value={breakdownLimit}
            onChange={(e) =>
              onLimitChange(Math.max(1, Math.min(50, Number(e.target.value) || 1)))
            }
            className="w-full text-xs p-1 rounded"
            style={{
              border: '1px solid var(--color-border)',
              background: 'var(--color-bg)',
            }}
          />
        </div>
      )}
    </div>
  );
}
